import React, { useState, useEffect } from 'react';
import { Property, PropertyType, PropertyStatus } from '../../types/property.ts';
import { formatCurrency } from '../../utils/formatters.ts';

interface PropertyFormProps {
  property?: Property | null;
  onSubmit: (data: Omit<Property, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onClose: () => void;
}

const PropertyForm: React.FC<PropertyFormProps> = ({ property, onSubmit, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [wasSubmitAttempted, setWasSubmitAttempted] = useState(false);
  const [formData, setFormData] = useState({
    name: property?.name || '',
    address: property?.address || '',
    property_type: property?.property_type || 'Apartment' as PropertyType,
    floor_unit: property?.floor_unit || '',
    area: property?.area || '',  // Change initial value from 0 to empty string
    planned_rent: property?.planned_rent || '', // Change initial value from 0 to empty string
    amenities: property?.amenities || [],
    status: property?.status || 'Vacant' as PropertyStatus, // Keep existing status if editing
    date_added: property?.date_added || new Date().toISOString().split('T')[0],
    notes: property?.notes || ''
  });

  // Prevent status changes if property has active tenant
  useEffect(() => {
    if (property?.tenant?.status === 'Active') {
      setFormData(prev => ({
        ...prev,
        status: 'Rented'
      }));
    }
  }, [property]);

  const amenitiesList = [
    'Air Conditioning',
    'Balcony',
    'Parking',
    'Security',
    'Power Backup',
    'Lift',
    'Water Supply',
    'Garden',
    'Gym',
    'Swimming Pool'
  ];

  const handleAmenityToggle = (amenity: string) => {
    const updatedAmenities = formData.amenities.includes(amenity)
      ? formData.amenities.filter(a => a !== amenity)
      : [...formData.amenities, amenity];
    
    setFormData({ ...formData, amenities: updatedAmenities });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) errors.name = 'This field is required';
    if (!formData.address.trim()) errors.address = 'This field is required';
    if (!formData.area || Number(formData.area) <= 0) errors.area = 'Please enter a valid area';
    if (!formData.planned_rent || Number(formData.planned_rent) <= 0) errors.planned_rent = 'Please enter a valid rent amount';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearError = (field: string) => {
    if (wasSubmitAttempted) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const MAX_RENT = 1000000;

  const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>, field: 'area' | 'planned_rent') => {
    const value = e.target.value;
    if (field === 'area') {
      // Only allow up to 4 digits for area
      if (value === '' || (/^\d*$/.test(value) && value.length <= 10)) {
        setFormData({ 
          ...formData, 
          [field]: value === '' ? '' : parseInt(value)
        });
        clearError(field);
      }
    } else {
      // For planned_rent, limit to MAX_RENT
      if (value === '' || /^\d*$/.test(value)) {
        const numValue = parseInt(value) || 0;
        const limitedValue = Math.min(numValue, MAX_RENT);
        setFormData({ 
          ...formData, 
          [field]: limitedValue || ''
        });
        clearError(field);
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    if (text.length <= 30) {
      setFormData({ ...formData, name: text });
      clearError('name');
    }
  };

  const handleFloorUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    if (text.length <= 30) {
      setFormData({ ...formData, floor_unit: text });
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 100) {
      setFormData({ ...formData, address: text });
      clearError('address');
    }
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 50) {
      setFormData({ ...formData, notes: text });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWasSubmitAttempted(true);
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submissionData = {
        ...formData,
        area: Number(formData.area),
        planned_rent: Number(formData.planned_rent)
      };
      await onSubmit(submissionData);
      onClose();
    } catch (error) {
      console.error('Error submitting property:', error);
      alert('Failed to save property');
    } finally {
      setLoading(false);
    }
  };

  // Add handler to prevent scroll propagation
  const handleModalWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  // Add ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="modal" onClick={onClose}>
      <div 
        className="property-form" 
        onClick={(e) => e.stopPropagation()}
        onWheel={handleModalWheel}
      >
        <div className="modal-header">
          <h2>{property ? 'Edit' : 'Add New'} Property</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          {/* Move form actions outside of form-grid */}
          <div className="form-grid">
            <div className="form-group">
              <label>Property Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={handleNameChange}
                required
              />
              {/* <span className="form-hint">
                {`${formData.name.length} of 30 characters`}
              </span> */}
              {validationErrors.name && (
                <span className="validation-message">{validationErrors.name}</span>
              )}
            </div>

            <div className="form-group">
              <label>Property Type *</label>
              <select
                value={formData.property_type}
                onChange={(e) => setFormData({ ...formData, property_type: e.target.value as PropertyType })}
                required
              >
                <option value="Apartment">Apartment</option>
                <option value="House">House</option>
                <option value="Commercial">Commercial</option>
              </select>
            </div>

            <div className="form-group">
              <label>Floor/Unit Number</label>
              <input
                type="text"
                value={formData.floor_unit}
                onChange={handleFloorUnitChange}
                placeholder="e.g., 4th Floor, Unit B"
              />
              {/* <span className="form-hint">
                {`${formData.floor_unit.length} of 30 characters`}
              </span> */}
            </div>

            <div className="form-group">
              <label>Area (sq. ft.) *</label>
              <input
                type="text" // Changed from number to text for better control
                value={formData.area}
                onChange={(e) => handleNumericInput(e, 'area')}
                min="1"
                maxLength={6}
              />
              {validationErrors.area && (
                <span className="validation-message">{validationErrors.area}</span>
              )}
            </div>

            <div className="form-group">
              <label>Planned Rent (₹) *</label>
              <input
                type="text"
                value={formData.planned_rent}
                onChange={(e) => handleNumericInput(e, 'planned_rent')}
                min="1"
                max={MAX_RENT}
              />
              {Number(formData.planned_rent) >= MAX_RENT && (
                <span className="form-hint">
                  Maximum allowed rent is {formatCurrency(MAX_RENT)}
                </span>
              )}
              {validationErrors.planned_rent && (
                <span className="validation-message">{validationErrors.planned_rent}</span>
              )}
            </div>

            <div className="form-group full-width">
              <label>Address *</label>
              <textarea
                value={formData.address}
                onChange={handleAddressChange}
                required
                rows={3}
                maxLength={100}
              />
              <span className="form-hint">
                {`${formData.address.length} of 100 characters`}
              </span>
              {validationErrors.address && (
                <span className="validation-message">{validationErrors.address}</span>
              )}
            </div>

            <div className="form-group full-width">
              <label>Amenities</label>
              <div className="amenities-grid">
                {amenitiesList.map((amenity) => (
                  <label key={amenity} className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.amenities.includes(amenity)}
                      onChange={() => handleAmenityToggle(amenity)}
                    />
                    {amenity}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group full-width">
              <label>Notes</label>
              <textarea
                value={formData.notes}
                onChange={handleNotesChange}
                rows={3}
                maxLength={50}
              />
              <span className="form-hint">
                {`${formData.notes.length} of 50 characters`}
              </span>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (property ? 'Update' : 'Add')} Property
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyForm;